import { Injectable, inject } from '@angular/core';
import { CoreHttpClient } from '@bepatient/core';
import { Observable, of, OperatorFunction, pipe } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { EDocs, EDocsFile, EDocsFolder } from './edocs.models';

@Injectable()
export class EDocsApiService {
  private http = inject(CoreHttpClient);

  loadEDocs(): Observable<EDocs> {
    return this.http
      .get<any>('/rest/users/myfiles', { withMobiCredentials: true })
      .pipe(this.eDocsParserV2());
  }

  saveFile(
    file: Partial<EDocsFile>,
    content: string,
    _fileName: string
  ): Observable<any> {
    return this.http.post<any>(
      '/rest/users/myfiles',
      {
        action: 'create',
        file_name: file.title,
        content: content,
        attachment_type: file.attachmentType
      },
      {
        withMobiCredentials: true
      }
    );
  }

  getDownloadFile(file: EDocsFile): Observable<any> {
    return this.http.get(`/rest/users/myfiles/${file.uuid}`, {
      withMobiCredentials: true
    });
  }

  downloadFile(file: EDocsFile): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(file.href, {
        withMobiCredentials: true
      })
      .pipe(this.http.downloadedBlobOperator());
  }

  saveFolder(folder: Partial<EDocsFolder>): Observable<any> {
    return this.http.post<any>(
      '/api/edocs/folders',
      { title: folder.title },
      { withMobiCredentials: true }
    );
  }

  deleteFile(file: EDocsFile): Observable<any> {
    return this.http.post<any>(
      '/rest/users/myfiles',
      {
        action: 'delete',
        attachment_id: file.uuid
      },
      {
        withMobiCredentials: true
      }
    );
  }

  updateFile(file: EDocsFile): Observable<any> {
    return this.http.post<any>(
      '/rest/users/myfiles',
      {
        action: 'update',
        attachment_id: file.uuid,
        file_name: file.title,
        attachment_type: file.attachmentType
      },
      {
        withMobiCredentials: true
      }
    );
  }

  updateFolder(folder: EDocsFolder): Observable<any> {
    return this.http.patch<any>(
      `/api/edocs/folders/${folder.uuid}`,
      { title: folder.title },
      { withMobiCredentials: true }
    );
  }

  deleteFolder(file: EDocsFolder): Observable<any> {
    return this.http.delete<any>(`/api/edocs/folders/${file.uuid}`, {
      withMobiCredentials: true
    });
  }

  private eDocsParserV2(): OperatorFunction<any, EDocs> {
    const getFilenameAndExtension = (filename: any) => {
      let filenameextension = filename.replace(/^.*[\\\/]/, '');
      let fileTitle = filenameextension.substring(
        0,
        filenameextension.lastIndexOf('.')
      );
      let ext = filenameextension.split('.').pop();
      return {
        fileName: fileTitle,
        ext
      };
    };
    const fileParser = (data: any): EDocsFile => ({
      uuid: data.id || '',
      title:
        (data.file_name && getFilenameAndExtension(data.file_name).fileName) ||
        '',
      extension:
        (data.file_name && getFilenameAndExtension(data.file_name).ext) || '',
      date: data.created_date,
      attachmentType: data.attachment_type || void 0,
      attachmentLabel: data.attachment_label || void 0,
      mimeType: data.type || void 0
    });

    const getFiles = (data: any): EDocsFile[] => {
      const files: EDocsFile[] = (data.payload || [])
        .filter(
          item =>
            item &&
            item.file_name &&
            getFilenameAndExtension(item.file_name).fileName &&
            getFilenameAndExtension(item.file_name).ext &&
            item.type
        )
        .map(item => fileParser(item));

      return (files.length && files) || void 0;
    };

    return pipe(
      map<any, EDocs>(data => ({
        files: getFiles(data)
      })),
      switchMap<EDocs, Observable<EDocs>>(eDocs => of(eDocs))
    );
  }

  /**
   * eDocs parser with file and folder
   * @returns {OperatorFunction<any, EDocs>}
   */
  // private eDocsParser(): OperatorFunction<any, EDocs> {
  //   const fileParser = (data: any, parentFolderUuid?: string): EDocsFile => ({
  //     uuid: data.uuid || '',
  //     title: data.title || '',
  //     href: data.meta.href || '', //(data.links && data.links.content && data.links.content.href) || '',
  //     date:
  //       data.meta && data.meta.ctime
  //         ? moment(data.meta.ctime, 'DD-MM-YYYY').format('YYYY-MM-DD')
  //         : '',
  //     lastAuthor: (data.meta && data.meta.last_author) || '',
  //     attachmentType: (data.meta && data.meta.attachment_type) || '',
  //     parentFolderUuid
  //   });

  //   const folderParser = (data: any): EDocsFolder => ({
  //     uuid: data.uuid || '',
  //     title: data.title || '',
  //     lastAuthor: (data.meta && data.meta.last_author) || ''
  //   });

  //   const getFiles = (data: any, parentFolderUuid?: string): EDocsFile[] => {
  //     const files: EDocsFile[] = (data.items || [])
  //       .filter(item => item.meta && item.meta.type === 'file')
  //       .map(item => fileParser(item, parentFolderUuid));

  //     return (files.length && files) || void 0;
  //   };

  //   const getFolders = (data: any): EDocsFolder[] => {
  //     const folders: EDocsFolder[] = (data.items || [])
  //       .filter(item => item.meta && item.meta.type === 'folder')
  //       .map(item => folderParser(item));

  //     return (folders.length && folders) || void 0;
  //   };

  //   return pipe(
  //     map<any, EDocs>(data => ({
  //       files: getFiles(data),
  //       folders: getFolders(data)
  //     })),
  //     switchMap<EDocs, Observable<EDocs>>(eDocs =>
  //       !eDocs.folders
  //         ? of(eDocs)
  //         : forkJoin<Observable<EDocsFolder>[]>(
  //             eDocs.folders.map(folder =>
  //               this.http
  //                 .get(`/api/edocs/folders/${folder.uuid}`, {
  //                   withMobiCredentials: true
  //                 })
  //                 .pipe(
  //                   map<any, EDocsFolder>(data => ({
  //                     ...folder,
  //                     files: getFiles(data, folder.uuid)
  //                   }))
  //                 )
  //             )
  //           ).pipe(
  //             map<EDocsFolder[], EDocs>(folders => ({ ...eDocs, folders }))
  //           )
  //     )
  //   );
  // }

  private mockedEDocs(): Observable<EDocs> {
    return of({
      files: [
        {
          uuid: 'uuid-file1',
          href: 'href',
          date: '20/07/20',
          title: 'Fichier 1',
          lastAuthor: 'Admin',
          attachmentType: 'file'
        },
        {
          uuid: 'uuid-file2',
          href: 'href',
          date: '20/07/20',
          title: 'Fichier 2',
          lastAuthor: 'Admin',
          attachmentType: 'file'
        },
        {
          uuid: 'uuid-file3',
          href: 'href',
          date: '20/07/20',
          title: 'Fichier 3',
          lastAuthor: 'Admin',
          attachmentType: 'file'
        }
      ],
      folders: [
        {
          uuid: 'uuid-folder',
          title: 'Répertoire vide',
          lastAuthor: 'Admin'
        },
        {
          uuid: 'uuid-folderA',
          title: 'Répertoire A',
          lastAuthor: 'Admin',
          files: [
            {
              uuid: 'uuid-folderA-file1',
              href: 'href',
              date: '20/07/20',
              title: 'Fichier A-1',
              lastAuthor: 'Admin',
              parentFolderUuid: 'uuid-folderA',
              attachmentType: 'file'
            },
            {
              uuid: 'uuid-folderA-file2',
              href: 'href',
              date: '20/07/20',
              title: 'Fichier A-2',
              lastAuthor: 'Admin',
              parentFolderUuid: 'uuid-folderA',
              attachmentType: 'file'
            }
          ]
        },
        {
          uuid: 'uuid-folderB',
          title: 'Répertoire B',
          lastAuthor: 'Admin',
          files: [
            {
              uuid: 'uuid-folderB-file1',
              href: 'href',
              date: '20/07/20',
              title: 'Fichier B-1',
              lastAuthor: 'Admin',
              parentFolderUuid: 'uuid-folderB',
              attachmentType: 'file'
            },
            {
              uuid: 'uuid-folderB-file2',
              href: 'href',
              date: '20/07/20',
              title: 'Fichier B-2',
              lastAuthor: 'Admin',
              parentFolderUuid: 'uuid-folderB',
              attachmentType: 'file'
            }
          ]
        }
      ]
    });
  }
}
